import * as THREE from "three";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Center, Environment, useGLTF } from "@react-three/drei";
import "./CSS_styles/aifun3d.css";

function CoffeeBean({
  scene,
  index,
  z,
  speed,
  pathMode,
  playing,
}) {
  const ref = useRef();
  const beanClone = useMemo(() => scene.clone(), [scene]);

  const { viewport, camera } = useThree();
  const { width, height } = viewport.getCurrentViewport(camera, [0, 0, -z]);

  const [data] = useState({
    x: THREE.MathUtils.randFloatSpread(2),
    y: THREE.MathUtils.randFloatSpread(height * 2),
    spin: THREE.MathUtils.randFloat(7, 12),
    rX: Math.random() * Math.PI,
    rY: Math.random() * Math.PI,
    rZ: Math.random() * Math.PI,
    offset: Math.random() * Math.PI * 2,
    radius: THREE.MathUtils.randFloat(0.6, 2.4),
    scale: THREE.MathUtils.randFloat(0.65, 1.05),
  });

  useFrame((state, dt) => {
    if (!ref.current || dt > 0.1) return;

    const t = state.clock.elapsedTime * speed + data.offset;

    if (playing) {
      if (pathMode === "up") {
        data.y += dt * speed * 2.2;
        if (data.y > height * 1.25) data.y = -height * 1.25;

        ref.current.position.set(data.x * width * 0.48, data.y, -z);
      }

      if (pathMode === "down") {
        data.y -= dt * speed * 2.2;
        if (data.y < -height * 1.25) data.y = height * 1.25;

        ref.current.position.set(data.x * width * 0.48, data.y, -z);
      }

      if (pathMode === "leftRight") {
        data.x += dt * speed * 0.95;
        if (data.x > 1.3) data.x = -1.3;

        ref.current.position.set(data.x * width * 0.65, data.y * 0.45, -z);
      }

      if (pathMode === "circle") {
        const circleX = Math.cos(t + index * 0.35) * (width * 0.18 + data.radius);
        const circleY = Math.sin(t + index * 0.35) * (height * 0.16 + data.radius * 0.6);

        ref.current.position.set(circleX, circleY, -z);
      }
    } else {
      ref.current.position.set(
        data.x * width * 0.3,
        Math.sin(state.clock.elapsedTime * 0.9 + index) * 0.15,
        -z
      );
    }

    ref.current.rotation.set(
      (data.rX += dt / data.spin),
      (data.rY += dt / (data.spin * 0.92)),
      (data.rZ += dt / data.spin)
    );
  });

  return (
    <group ref={ref} scale={data.scale}>
      <Center>
        <primitive object={beanClone} />
      </Center>
    </group>
  );
}

function CoffeeBeansStage({
  pathMode,
  playing,
  overlay = false,
  count = 22,
  depth = 42,
  speed = 1.15,
  easing = (x) => Math.sqrt(1 - Math.pow(x - 1, 2)),
}) {
  const { scene } = useGLTF("/coffee_bean.glb");

  return (
    <div className={`aifun3d-asmr-stage ${overlay ? "is-overlay" : ""}`}>
      <Canvas
        flat
        gl={{ antialias: false, alpha: true }}
        dpr={[1, 1.5]}
        camera={{ position: [0, 0, 10], fov: 22, near: 0.01, far: depth + 20 }}
        style={{ background: "transparent" }}
      >
        {!overlay && <color attach="background" args={["#e6c9aa"]} />}

        <ambientLight intensity={1.45} />
        <directionalLight position={[5, 6, 5]} intensity={2.2} />
        <directionalLight position={[-4, -2, 3]} intensity={0.85} />
        <Environment preset="city" background={false} />

        {Array.from({ length: count }, (_, i) => (
          <CoffeeBean
            key={i}
            scene={scene}
            index={i}
            z={Math.round(easing(i / count) * depth)}
            speed={speed}
            pathMode={pathMode}
            playing={playing}
          />
        ))}
      </Canvas>
    </div>
  );
}

useGLTF.preload("/coffee_bean.GLB");

export default function AIFun3D() {
  const [beanTiming, setBeanTiming] = useState(10);
  const [beanPath, setBeanPath] = useState("up");
  const [asmrPlaying, setAsmrPlaying] = useState(false);

  const asmrTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      window.clearTimeout(asmrTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!asmrPlaying) return;

    window.clearTimeout(asmrTimerRef.current);

    asmrTimerRef.current = window.setTimeout(() => {
      setAsmrPlaying(false);
    }, beanTiming * 1000);

    return () => {
      window.clearTimeout(asmrTimerRef.current);
    };
  }, [asmrPlaying, beanTiming]);

  const startAsmrGame = () => {
    window.clearTimeout(asmrTimerRef.current);
    setAsmrPlaying(false);

    requestAnimationFrame(() => {
      setAsmrPlaying(true);
    });
  };

  return (
    <section className="aifun3d-section" data-section="ai-fun">
      <div className="aifun3d-shell">
        <div className="aifun3d-header">
          
          <h2 className="aifun3d-title">Projects / ASMR</h2>
          
        
        </div>

        <div className="aifun3d-layout">
          <div className="aifun3d-row">
            <div className="aifun3d-left">
              <a 
                href="https://github.com/Ankith5R" 
                target="_blank" 
                rel="noreferrer"
                className="aifun3d-trigger aifun3d-trigger-live"
                style={{cursor: "pointer", textDecoration: "none"}}
              >
                <div className="aifun3d-trigger-body">
                  <div className="aifun3d-trigger-icon">◉</div>
                  <div className="aifun3d-trigger-copy">
                    <div className="aifun3d-trigger-title">GitHub</div>
                    <div className="aifun3d-trigger-sub">View projects</div>
                  </div>
                </div>
              </a>
            </div>

            <article className="aifun3d-card aifun3d-card-featured">
              <div className="aifun3d-card-head">
                <span className="aifun3d-badge">CODE</span>
                
                <h3>AI Projects</h3>
              </div>
              

              <p className="aifun3d-card-copy">
                My work spans  LLMs, deep learning, Computer Vision, LangChain, BLIP, and 
                multimodal AI, where I build end-to-end intelligent systems 
                for real-world applications.<br/>
                <strong>Check it out on GitHub.</strong>
              </p>
            </article>
          </div>

          {/* ASMR Coffee Game */}
          <div className="aifun3d-row aifun3d-row-asmr">
            <div className="aifun3d-left">
              <button
                type="button"
                className="aifun3d-trigger aifun3d-trigger-asmr"
                data-active={asmrPlaying}
                onClick={startAsmrGame}
              >
                <div className="aifun3d-trigger-body">
                  <div className="aifun3d-trigger-icon">●</div>
                  <div className="aifun3d-trigger-copy">
                    <div className="aifun3d-trigger-title">Click Here</div>
                    <div className="aifun3d-trigger-sub">
                      {asmrPlaying ? "Playing..." : "Launch coffee beans"}
                    </div>
                  </div>
                </div>
              </button>
            </div>

            <article className="aifun3d-card aifun3d-card-asmr">
              <div className="aifun3d-card-head">
                <span className="aifun3d-badge">PLAY</span>
                <h3>ASMR Coffee Bean Game</h3>
              </div>

              <p className="aifun3d-card-copy">
                How to launch coffee beans across the screen: <br/>
                {/* Stay focused as coffee beans glide across the screen. */}
                1. Set how long the beans appear using the duration buttons below. <br/>
                2. Choose a path—up, down, left to right, or circular.<br/>
                3. When everything looks good, click the button on the left to launch the beans. <br/>
                (Please click the left button after selecting duration and path, even if nothing is visible in the window below.)
                <br/>Coming soon: Donuts!
              </p>

              <div className="aifun3d-controls">
                <div className="aifun3d-control-group">
                  <span className="aifun3d-control-label">Duration</span>
                  <div className="aifun3d-chip-row">
                    {[5, 10, 15, 20].map((value) => (
                      <button
                        key={value}
                        type="button"
                        className="aifun3d-chip"
                        data-active={beanTiming === value}
                        onClick={() => setBeanTiming(value)}
                      >
                        {value}s
                      </button>
                    ))}
                  </div>
                </div>

                <div className="aifun3d-control-group">
                  <span className="aifun3d-control-label">Choose Path and click button on left.</span>
                  <div className="aifun3d-chip-row">
                    <button
                      type="button"
                      className="aifun3d-chip"
                      data-active={beanPath === "up"}
                      onClick={() => setBeanPath("up")}
                    >
                      Down → Up
                    </button>
                    <button
                      type="button"
                      className="aifun3d-chip"
                      data-active={beanPath === "down"}
                      onClick={() => setBeanPath("down")}
                    >
                      Up → Down
                    </button>
                    <button
                      type="button"
                      className="aifun3d-chip"
                      data-active={beanPath === "leftRight"}
                      onClick={() => setBeanPath("leftRight")}
                    >
                      Left → Right
                    </button>
                    <button
                      type="button"
                      className="aifun3d-chip"
                      data-active={beanPath === "circle"}
                      onClick={() => setBeanPath("circle")}
                    >
                      Circular
                    </button>
                  </div>
                </div>
              </div>

              <div className="aifun3d-stage-wrap">
                {asmrPlaying ? (
                  <div className="aifun3d-stage-empty">
                    Coffee beans are floating across the page for &nbsp;
                    <strong>{beanTiming}s</strong>. You can keep scrolling while
                    the effect continues.
                  </div>
                ) : (
                  <Suspense
                    fallback={
                      <div className="aifun3d-stage-empty">
                        Loading coffee beans...
                      </div>
                    }
                  >
                    <CoffeeBeansStage
                      pathMode={beanPath}
                      duration={beanTiming}
                      playing={false}
                    />
                  </Suspense>
                )}
              </div>
            </article>
          </div>

          {/* Gaming */}
          <div className="aifun3d-row">
            <div className="aifun3d-left">
              <button
                type="button"
                className="aifun3d-trigger aifun3d-trigger-soon"
              >
                <div className="aifun3d-trigger-body">
                  <div className="aifun3d-trigger-icon">◎</div>
                  <div className="aifun3d-trigger-copy">
                    <div className="aifun3d-trigger-title">Gaming</div>
                    <div className="aifun3d-trigger-sub">Coming soon</div>
                  </div>
                </div>
              </button>
            </div>

            <article className="aifun3d-card">
              <div className="aifun3d-card-head">
                <span className="aifun3d-badge aifun3d-badge-muted">SOON</span>
                <h3>AR / VR game</h3>
              </div>

              <p className="aifun3d-card-copy">
                Immersive gaming experience combining AR/VR technologies with AI. 
                <br/>Next-generation interactive gaming platform in development.
              </p>
            </article>
          </div>
        </div>
      </div>

      {asmrPlaying && (
        <div className="aifun3d-asmr-overlay" aria-hidden="true">
          <Suspense fallback={null}>
            <CoffeeBeansStage
              pathMode={beanPath}
              duration={beanTiming}
              playing={true}
              overlay={true}
            />
          </Suspense>
        </div>
      )}
    </section>
  );
}